import React, { useState, useEffect, useMemo } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { Product, formatPublishDate } from '../../types/product';
import { productService } from '../../services/ProductService';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';
import OptimizeDialog from './OptimizeDialog';
import { Input } from '../ui/input';
import InfiniteScroll from 'react-infinite-scroll-component';
import { formatStoreName } from '../../utils/formatters';

const MarketplaceIndicator: React.FC<{
  letter: string;
  isOptimized: boolean;
  product: Product;
  onRefresh?: () => void;
  onOptimize?: (productId: string, marketplace: string) => Promise<any>;
}> = ({ letter, isOptimized, product, onRefresh, onOptimize }) => {
  const [showOptimizeDialog, setShowOptimizeDialog] = useState(false);
  const [showReoptimizeConfirm, setShowReoptimizeConfirm] = useState(false);
  
  const marketplace =
    letter === 'S' ? 'Shopify' :
    letter === 'G' ? 'Google Shopping' : 'Amazon';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOptimized) {
      setShowReoptimizeConfirm(true);
    } else {
      setShowOptimizeDialog(true);
    }
  };

  const handleOptimizeComplete = async () => {
    setShowOptimizeDialog(false);
    // First wait a bit to ensure the optimization has completed
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Then refresh the product list
    if (onRefresh) {
      await onRefresh();
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-colors
          ${isOptimized ?
            'bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer' :
            'bg-red-100 text-red-800 hover:bg-red-200 cursor-pointer'
          }`}
        title={`${isOptimized ? 'Click to re-optimize' : 'Click to optimize'} for ${marketplace}`}
      >
        {letter}
      </button>

      <ReoptimizeConfirmDialog
        isOpen={showReoptimizeConfirm}
        onClose={() => setShowReoptimizeConfirm(false)}
        onConfirm={() => {
          setShowReoptimizeConfirm(false);
          setShowOptimizeDialog(true);
        }}
        marketplace={marketplace}
      />

      <OptimizeDialog
        isOpen={showOptimizeDialog}
        onClose={() => setShowOptimizeDialog(false)}
        product={product}
        marketplace={marketplace}
        onOptimizeComplete={handleOptimizeComplete}
      />
    </>
  );
};

const ReoptimizeConfirmDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  marketplace: string;
}> = ({ isOpen, onClose, onConfirm, marketplace }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold mb-4">Confirm Re-optimization</h2>
        <p className="text-gray-600 mb-6">
          This product has been optimized for {marketplace}. Are you sure you want to run optimization again?
        </p>
        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
          >
            No Thanks
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            Yes, Please
          </button>
        </div>
      </div>
    </div>
  );
};

interface ProductListProps {
  products: Product[];
  onSelectProduct: (productId: string) => void;
  selectedProductId: string | null;
  maxHeight?: string;
  onRefresh?: () => void;
  compact?: boolean;
  showSearch?: boolean;
  showFilters?: boolean;
  onOptimize?: (productId: string, marketplace: string) => Promise<any>;
  onSelectionChange?: (selectedIds: string[]) => void;
  showCheckboxes?: boolean;
}

export interface ProductListRef {
  clearSelections: () => void;
}

const ProductList = React.forwardRef<ProductListRef, ProductListProps>(({
  products,
  onSelectProduct,
  selectedProductId,
  maxHeight = '70vh',
  onRefresh,
  onOptimize,
  compact = false,
  showSearch = true,
  showFilters = true,
  onSelectionChange,
  showCheckboxes = true
}, ref) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [shopifyStoreFilter, setShopifyStoreFilter] = useState('All Stores');
  const [optimizationFilter, setOptimizationFilter] = useState('All');
  const [publishedFilter, setPublishedFilter] = useState('All');
  const [error, setError] = useState<string | null>(null);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [displayedProducts, setDisplayedProducts] = useState<Product[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());

  // Expose method to clear selections
  React.useImperativeHandle(ref, () => ({
    clearSelections: () => {
      setSelectedProductIds(new Set());
      if (onSelectionChange) {
        onSelectionChange([]);
      }
    }
  }));

  // Add event listener for import completion
  React.useEffect(() => {
    const handleImportComplete = () => {
      if (onRefresh) {
        onRefresh();
      }
    };

    window.addEventListener('importComplete', handleImportComplete);
    return () => {
      window.removeEventListener('importComplete', handleImportComplete);
    };
  }, [onRefresh]);

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Search filter
      const searchTerm = searchQuery.toLowerCase();
      if (searchQuery && !product.basicInfo.title.toLowerCase().includes(searchTerm) && 
          !product.basicInfo.sku?.toLowerCase().includes(searchTerm)) {
        return false;
      }

      // Source filter
      if (sourceFilter !== 'All' && product.source !== sourceFilter) {
        return false;
      }

      // Store filter (only for Shopify products)
      if (sourceFilter === 'Shopify' && shopifyStoreFilter !== 'All Stores' && 
          formatStoreName(product.storeName) !== shopifyStoreFilter) {
        return false;
      }

      // Optimization filter
      const hasOptimizations = Object.values(product.optimizations || {}).some(opt => opt?.title);
      if (optimizationFilter === 'Optimized' && !hasOptimizations) {
        return false;
      }
      if (optimizationFilter === 'NotOptimized' && hasOptimizations) {
        return false;
      }

      // Published status filter
      const isPublished = !!product.publishTimestamps?.shopify;
      if (publishedFilter === 'Published' && !isPublished) {
        return false;
      }
      if (publishedFilter === 'Not Published' && isPublished) {
        return false;
      }

      return true;
    });
  }, [products, searchQuery, sourceFilter, shopifyStoreFilter, optimizationFilter, publishedFilter]);

  useEffect(() => {
    // Reset displayed products when filters change
    setDisplayedProducts(filteredProducts.slice(0, 20));
    setHasMore(filteredProducts.length > 20);
  }, [filteredProducts]);

  const loadMore = () => {
    const currentLength = displayedProducts.length;
    const nextBatch = filteredProducts.slice(currentLength, currentLength + 20);
    setDisplayedProducts(prev => [...prev, ...nextBatch]);
    setHasMore(currentLength + 20 < filteredProducts.length);
  };

  return (
    <div className="bg-white rounded-lg">
      {!compact && (
        <>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-4">
            {/* Search Input */}
            {showSearch && (
              <div className="relative w-full md:w-64">
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            )}
            
            {showFilters && (
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full">
                {/* Source Filter */}
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="w-full md:w-40 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="All">All Sources</option>
                  <option value="Shopify">Shopify</option>
                  <option value="Amazon">Amazon</option>
                </select>

                {/* Store Filter */}
                <select
                  value={shopifyStoreFilter}
                  onChange={(e) => setShopifyStoreFilter(e.target.value)}
                  disabled={sourceFilter !== 'Shopify'}
                  className="w-full md:w-48 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="All Stores">All Stores</option>
                  {Array.from(new Set(products
                    .filter(p => p.storeName)
                    .map(p => formatStoreName(p.storeName))
                    .filter(Boolean)
                    .sort()
                  )).map(store => (
                    <option key={store} value={store}>{store}</option>
                  ))}
                </select>

                {/* Optimization Filter */}
                <select
                  value={optimizationFilter}
                  onChange={(e) => setOptimizationFilter(e.target.value)}
                  className="w-full md:w-44 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="All">All Optimization</option>
                  <option value="Optimized">Optimized</option>
                  <option value="NotOptimized">Not Optimized</option>
                </select>

                {/* Published Status Filter */}
                <select
                  value={publishedFilter}
                  onChange={(e) => setPublishedFilter(e.target.value)}
                  className="w-full md:w-44 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="All">All Publish Status</option>
                  <option value="Published">Published</option>
                  <option value="Not Published">Not Published</option>
                </select>
              </div>
            )}
          </div>
          <div className="text-sm text-gray-500 mb-4">
            {filteredProducts.length} products found
          </div>
        </>
      )}

      <InfiniteScroll
        dataLength={displayedProducts.length}
        next={loadMore}
        hasMore={hasMore}
        loader={<></>}
        scrollableTarget="scrollableDiv"
        className="divide-y"
      >
        <div 
          id="scrollableDiv"
          className="divide-y overflow-y-auto"
          style={{ maxHeight }}
        >
          {displayedProducts
            .filter((product, index, self) => 
              index === self.findIndex(p => p.id === product.id)
            )
            .map(product => (
              <div key={product.id} className="flex flex-col md:flex-row items-start relative">
                {/* Product Info - Clickable */}
                <button
                  onClick={() => onSelectProduct(product.id)}
                  className={`flex-1 text-left p-4 hover:bg-gray-50 transition-colors ${
                    selectedProductId === product.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex flex-col md:flex-row justify-between items-start gap-2">
                    <div className="w-full md:flex-1">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900 text-sm">{product.basicInfo.title}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>{formatStoreName(product.storeName)}</span>
                        {product.basicInfo.sku && (
                          <>
                            <span>•</span>
                            <span>{product.basicInfo.sku}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2 md:mt-0">
                      {/* Status Badge */}
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        product.status === 'Active' ? 'bg-green-100 text-green-800' :
                        product.status === 'Inactive' ? 'bg-yellow-100 text-yellow-800' :
                        product.status === 'Error' ? 'bg-red-100 text-red-800' :
                        product.status === 'Needs Review' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {product.status}
                      </span>
                      
                      {/* Source Badge */}
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        {product.source}
                        {product.source === 'Shopify' && product.storeName && (
                          ` - ${formatStoreName(product.storeName)}`
                        )}
                      </span>
                    </div>
                  </div>
                </button>

                {/* Actions - Not Clickable */}
                <div className="flex items-center gap-3 p-4 md:border-l border-t md:border-t-0">
                  {showCheckboxes && (
                    <input
                      type="checkbox"
                      checked={selectedProductIds.has(product.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        const newSelection = new Set(selectedProductIds);
                        if (e.target.checked) {
                          newSelection.add(product.id);
                        } else {
                          newSelection.delete(product.id);
                        }
                        setSelectedProductIds(newSelection);
                        if (onSelectionChange) {
                          onSelectionChange(Array.from(newSelection));
                        }
                      }}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <div className="flex items-center gap-1.5">
                    <MarketplaceIndicator 
                      letter="S" 
                      isOptimized={!!product.optimizations?.shopify?.title}
                      product={product}
                      onRefresh={onRefresh}
                      onOptimize={onOptimize}
                    />
                    {product.publishTimestamps?.shopify && (
                      <div 
                        className="text-xs text-gray-500" 
                        title={product.publishTimestamps?.shopify ? `Published: ${formatPublishDate(product.publishTimestamps.shopify)}` : 'Not published'}
                      >
                        ✓
                      </div>
                    )}
                  </div>
                  <MarketplaceIndicator 
                    letter="G" 
                    isOptimized={!!product.optimizations?.googleShopping?.title}
                    product={product}
                    onRefresh={onRefresh}
                    onOptimize={onOptimize}
                  />
                  <MarketplaceIndicator
                    letter="A"
                    isOptimized={!!product.optimizations?.amazon?.title}
                    product={product}
                    onRefresh={onRefresh}
                    onOptimize={onOptimize}
                  />
                  {/* Delete Button - Moved to end with extra spacing */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setProductToDelete(product);
                    }}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors ml-2"
                    title="Delete product"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
        </div>
      </InfiniteScroll>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!productToDelete}
        onClose={() => setProductToDelete(null)}
        onConfirm={async () => {
          if (!productToDelete) return;
          
          try {
            await productService.deleteProduct(productToDelete.id);
            if (onRefresh) {
              await onRefresh();
            }
          } catch (error) {
            console.error('Error deleting product:', error);
            setError('Failed to delete product');
          }
          setProductToDelete(null);
        }}
        title="Delete Product"
        description="Are you sure you want to remove this product from the LMS app? This will only delete it from this app and will not affect your Shopify store. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <span className="block sm:inline">{error}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4"
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
});

ProductList.displayName = 'ProductList';

export default ProductList;
